import { useEffect, useMemo, useState } from 'react';
import { BookOpenText, Home, Sprout, UserRound } from 'lucide-react';
import { HomeView } from './views/HomeView';
import { LearningView } from './views/LearningView';
import { ProfileView } from './views/ProfileView';
import { LoginPage } from './views/ProfileView';
import { ChildAvatar, Modal, Toast } from './components/ui';
import type { AppState, AppView, KnowledgeType, ProfilePage, ReviewResult } from './types';
import { loadInitialState, persistState } from './lib/storage';
import { getDueCount, getPlanCount, performReview } from './lib/srs';

const AUTH_KEY = 'memo-parent-auth';

const typeMeta: Array<{ type: KnowledgeType; label: string; emoji: string; note: string }> = [
  { type: 'character', label: '汉字记忆', emoji: '汉', note: '看字形，读字音' },
  { type: 'poem', label: '古诗记忆', emoji: '诗', note: '读诗句，想画面' },
  { type: 'word', label: '英文记忆', emoji: 'A', note: '看图片，记单词' }
];

export default function App() {
  const [state, setState] = useState<AppState | null>(null);
  const [view, setView] = useState<AppView>('home');
  const [profilePage, setProfilePage] = useState<ProfilePage>('menu');
  const [learningType, setLearningType] = useState<KnowledgeType>('character');
  const [startOpen, setStartOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem(AUTH_KEY) === 'true');

  useEffect(() => {
    if (!loggedIn) {
      setState(null);
      return;
    }
    void loadInitialState().then(setState);
  }, [loggedIn]);

  const handleLoginChange = (value: boolean) => {
    setLoggedIn(value);
    if (value) localStorage.setItem(AUTH_KEY, 'true');
    else localStorage.removeItem(AUTH_KEY);
  };

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const activeChild = useMemo(() => {
    if (!state) return undefined;
    return state.children.find((child) => child.id === state.activeChildId) ?? state.children[0];
  }, [state]);

  const updateState = (updater: (current: AppState) => AppState) => {
    setState((current) => {
      if (!current) return current;
      const next = updater(current);
      persistState(next);
      return next;
    });
  };

  const showToast = (message: string) => setToast(message);

  const handleSelectChild = (childId: string) => {
    updateState((current) => ({ ...current, activeChildId: childId, updatedAt: new Date().toISOString() }));
  };

  const handleStart = (type: KnowledgeType) => {
    if (!state?.children.length || !activeChild) {
      setStartOpen(false);
      setProfilePage('children');
      setView('profile');
      showToast('请先添加一名儿童');
      return;
    }
    if (getPlanCount(state, type, activeChild.id) === 0) {
      setStartOpen(false);
      setProfilePage('plan');
      setView('profile');
      showToast('请先把知识加入学习计划');
      return;
    }
    setLearningType(type);
    setStartOpen(false);
    setView('learn');
  };

  const handleReview = (itemId: string, result: ReviewResult) => {
    if (!state || !activeChild) return null;
    const outcome = performReview(state, activeChild.id, itemId, result);
    setState(outcome.state);
    persistState(outcome.state);
    return outcome.event;
  };

  if (!loggedIn) {
    return (
      <> 
        <LoginPage onLoginChange={handleLoginChange} onToast={showToast} />
        <Toast message={toast} />
      </>
    );
  }

  if (!state) {
    return (
      <div className="app-loading">
        <div className="mascot-seed" aria-hidden="true">🌱</div>
        <strong>正在打开记忆岛...</strong>
      </div>
    );
  }

  const navItems: Array<{ view: AppView; label: string; icon: typeof Home }> = [
    { view: 'home', label: '首页', icon: Home },
    { view: 'learn', label: '开始学习', icon: Sprout },
    { view: 'profile', label: '我的', icon: UserRound }
  ];

  if (view === 'learn' && activeChild) {
    return (
      <>
        <LearningView
          state={state}
          child={activeChild}
          type={learningType}
          onSelectChild={handleSelectChild}
          onExit={() => setView('home')}
          onReview={handleReview}
          onToast={showToast}
        />
        <Toast message={toast} />
      </>
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" type="button" onClick={() => setView('home')} aria-label="返回首页">
          <span className="brand__mark">
            <BookOpenText size={20} />
          </span>
          <span>
            <strong>小芽记忆岛</strong>
            <small>每天一点点，记得更牢</small>
          </span>
        </button>
        {state.children.length > 1 ? (
          <div className="child-switcher" aria-label="切换儿童">
            {state.children.map((child) => (
              <button
                key={child.id}
                type="button"
                className={child.id === activeChild?.id ? 'is-active' : ''}
                onClick={() => handleSelectChild(child.id)}
              >
                <ChildAvatar child={child} size="small" active={child.id === activeChild?.id} />
                <span>{child.name}</span>
              </button>
            ))}
          </div>
        ) : activeChild ? (
          <button className="single-child-pill" type="button" onClick={() => {
            setView('profile');
            setProfilePage('children');
          }}>
            <ChildAvatar child={activeChild} size="small" />
            {activeChild.name}
          </button>
        ) : null}
      </header>

      <main className="app-content">
        {view === 'home' ? (
          <HomeView
            state={state}
            activeChild={activeChild}
            onSelectChild={handleSelectChild}
            onStart={() => setStartOpen(true)}
            onOpenProfile={(page) => {
              setProfilePage(page);
              setView('profile');
            }}
          />
        ) : (
          <ProfileView
            state={state}
            activeChildId={activeChild?.id ?? null}
            page={profilePage}
            loggedIn={loggedIn}
            updateState={updateState}
            onNavigate={setProfilePage}
            onSelectChild={handleSelectChild}
            onLoginChange={handleLoginChange}
            onToast={showToast}
          />
        )}
      </main>

      <nav className="bottom-nav" aria-label="主导航">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isLearn = item.view === 'learn';
          const active = item.view === view;
          return (
            <button
              key={item.view}
              type="button"
              className={`${active ? 'is-active' : ''} ${isLearn ? 'bottom-nav__learn' : ''}`}
              onClick={() => {
                if (isLearn) {
                  if (!state.children.length) {
                    setProfilePage('children');
                    setView('profile');
                    showToast('请先添加儿童');
                  } else {
                    setStartOpen(true);
                  }
                } else {
                  setView(item.view);
                  if (item.view === 'profile') setProfilePage('menu');
                }
              }}
            >
              <span className="bottom-nav__icon"><Icon size={22} /></span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <Modal
        open={startOpen}
        title="今天想记什么？"
        subtitle={activeChild ? `${activeChild.name}，挑一颗知识种子吧` : '请先选择儿童'}
        onClose={() => setStartOpen(false)}
        size="large"
      >
        {state.children.length > 1 ? (
          <div className="learning-child-picker">
            {state.children.map((child) => (
              <button
                key={child.id}
                type="button"
                className={child.id === activeChild?.id ? 'is-active' : ''}
                onClick={() => handleSelectChild(child.id)}
              >
                <ChildAvatar child={child} size="medium" active={child.id === activeChild?.id} />
                <span>
                  <strong>{child.name}</strong>
                  <small>{getDueCount(state, child.id)} 项待复习</small>
                </span>
              </button>
            ))}
          </div>
        ) : null}
        <div className="learning-type-grid">
          {typeMeta.map((type) => {
            const due = activeChild ? getDueCount(state, activeChild.id, type.type) : 0;
            const planCount = activeChild ? getPlanCount(state, type.type, activeChild.id) : 0;
            return (
              <button
                key={type.type}
                type="button"
                className={`learning-type-card learning-type-card--${type.type}`}
                onClick={() => handleStart(type.type)}
              >
                <span className="learning-type-card__symbol">{type.emoji}</span>
                <strong>{type.label}</strong>
                <small>{type.note}</small>
                <b>{planCount ? due ? `${due} 项待复习` : '计划内暂无到期' : '尚未加入学习计划'}</b>
              </button>
            );
          })}
        </div>
      </Modal>

      <Toast message={toast} />
    </div>
  );
}
